/**
 * Where the patient is, when they have chosen to share it.
 *
 * Distance to a provider is only honest if both halves are real: the
 * patient's own position, which only the browser can give and only with
 * permission, and the provider's coordinates, which the directory records as
 * `geo`. Neither is invented here — a missing half means no distance is shown
 * at all, rather than a plausible-looking number.
 *
 * The position is kept in `localStorage` so the patient is not re-prompted on
 * every page, and it is never sent anywhere: it is used to compute a distance
 * in the browser and nothing else.
 */

const KEY = 'nhc_patient_coords';
/** Older than this and it is not where the patient is any more. */
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

export interface Coords {
  lat: number;
  lng: number;
  at: number;
}

type Listener = (c: Coords | null) => void;
const listeners = new Set<Listener>();

export function getCoords(): Coords | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const c = JSON.parse(raw) as Coords;
    if (typeof c?.lat !== 'number' || typeof c?.lng !== 'number') return null;
    if (Date.now() - (c.at ?? 0) > MAX_AGE_MS) return null;
    return c;
  } catch {
    return null;
  }
}

function publish(c: Coords | null) {
  listeners.forEach((fn) => fn(c));
}

export function onCoordsChanged(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function clearCoords(): void {
  try { localStorage.removeItem(KEY); } catch { /* private mode */ }
  publish(null);
}

/**
 * Ask the browser once. Resolves to null when there is no geolocation, when
 * the patient declines, or when the fix times out — all of which are answers,
 * not errors.
 */
export function requestCoords(): Promise<Coords | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c: Coords = { lat: pos.coords.latitude, lng: pos.coords.longitude, at: Date.now() };
        try { localStorage.setItem(KEY, JSON.stringify(c)); } catch { /* private mode */ }
        publish(c);
        resolve(c);
      },
      () => { publish(null); resolve(null); },
      { timeout: 8000, maximumAge: 5 * 60 * 1000 },
    );
  });
}

/** Great-circle distance in km. */
export function distanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

/**
 * "1.2 km away" / "820 m away", or null when either side has no position.
 * A provider with no `geo` in the directory simply has no distance.
 */
export function distanceLabel(
  me: Coords | null,
  provider: { latitude?: number; longitude?: number },
): string | null {
  if (!me) return null;
  const { latitude, longitude } = provider;
  if (typeof latitude !== 'number' || typeof longitude !== 'number') return null;
  const km = distanceKm(me, { lat: latitude, lng: longitude });
  if (!Number.isFinite(km)) return null;
  return km < 1 ? `${Math.round(km * 1000)} m away` : `${km.toFixed(1)} km away`;
}
